import type { DivisionDto } from 'shared/dtos/divisions.dto';
import type { DivisionFormFormikState } from 'shared/interfaces/divisions.interface';

import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from 'components/buttons/button/button';
import { CreateButton } from 'components/buttons/create-button/create-button';
import { InputField } from 'components/fields/input-field/input-field';
import { TextEditor } from 'components/fields/text-editor/text-editor';
import { GrayPanel } from 'components/panels/gray-panel/gray-panel';
import { Container } from 'components/template/container/container';
import { FixedHeader } from 'components/template/fixed-header/fixed-header';
import { FormBody } from 'components/template/form-body/form-body';
import { FormHeader } from 'components/template/form-header/FormHeader';
import { ConfirmationModal } from 'components/template/modals/confirmation-modal';
import { NotFoundMessage } from 'components/template/not-found-message/not-found-message';
import { Spinner } from 'components/template/spinner/spinner';
import { PermissionsContext } from 'contexts';
import { Form, Formik } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import ReportService from 'services/reports.service';
import events from 'shared/constants/events';
import { ASSIGN_PEOPLE_MODAL, MODAL_DIVISION } from 'shared/constants/modals';
import { validateField } from 'shared/utils';
import CheckIfFormIsDirty from 'shared/utils/checkIfFormIsDirty';
import { useAppDispatch, useAppSelector } from 'store';
import { getAgenciesWithDivisionsThunk } from 'store/agencies/agencies.thunks';
import {
  removeDivisionFromStore,
  setCurrentDivision,
  setDivisionEnvironments,
  setDivisionMissions,
} from 'store/divisions/divisions.slice';
import {
  createDivisionThunk,
  getDivisionsThunk,
  removeDivisionThunk,
  updateDivisionThunk,
} from 'store/divisions/divisions.thunks';
import { closeModal, openModal } from 'store/modals/modals.slice';
import { getPeopleThunk } from 'store/people/people.thunks';
import { vocabulary } from 'vocabulary/german';

import DivisionFormActivities from './division-form-activities/division-form-activities';
import { DivisionFormStg } from './division-form-stg/division-form-stg';

const reportsService = new ReportService();

const divisionToFormValues = (
  division: DivisionDto,
  fallback: DivisionFormFormikState,
): DivisionFormFormikState => ({
  id: division.id,
  title: division.title || '',
  missions: division.missions || '',
  environments: division.environments || '',
  PeopleId: division.PeopleId ?? null,
  AgencyId: division.AgencyId ?? null,
  Activities: division.Activities ?? fallback.Activities ?? [],
  ShortTermGoals: division.ShortTermGoals ?? fallback.ShortTermGoals ?? [],
});

export const DivisionForm = () => {
  const dispatch = useAppDispatch();
  const { permissions } = useContext(PermissionsContext);
  const [locked, setLocked] = useState(false);
  const [missionsFocusPosition, setMissionsFocusPosition] = useState(0);
  const [environmentsFocusPosition, setEnvironmentsFocusPosition] = useState(0);
  const setFieldValueRef = useRef<
    ((field: string, value: unknown) => void) | null
  >(null);

  const divisions = useAppSelector((state) => state.divisions);
  const agencies = useAppSelector((state) => state.agencies);
  const people = useAppSelector((state) => state.people);
  const yearId = useAppSelector((state) => state.years.currentYearId);
  const yearName = useAppSelector((state) => state.years.currentYearName);
  const currentYearFrozen = useAppSelector(
    (state) => state.years.currentYearFrozen,
  );
  const currentYearFrozenLocal = Boolean(
    localStorage.getItem('currentYearFrozen') === 'true',
  );
  const shouldChangeAssign = currentYearFrozenLocal
    ? permissions.freezeAssign === true
    : permissions.assign === true;

  const initialValues: DivisionFormFormikState = {
    id: divisions.current?.id || null,
    title: divisions.current?.title || '',
    missions: divisions.current?.missions || '',
    environments: divisions.current?.environments || '',
    PeopleId: divisions.current?.PeopleId || null,
    AgencyId: divisions.current?.AgencyId || null,
    Activities: divisions.current?.Activities || [],
    ShortTermGoals: divisions.current?.ShortTermGoals || [],
  };

  if (people.selected.forDivision.peopleId) {
    initialValues.PeopleId = people.selected.forDivision.peopleId;
  }

  const formKey = `${divisions.current?.id ?? 'new'}-${people.selected.forDivision.peopleId ?? 'none'}`;

  const onSubmit = (id: number | null, values: DivisionFormFormikState) => {
    if (divisions.isCreateUpdateDivisionThunkPending) {
      return null;
    }

    if (id) {
      // when we UPDATE the Divisions behavior
      return dispatch(updateDivisionThunk({ ...values, id })).unwrap();
    }

    // when we CREATE the Divisions behavior
    dispatch(createDivisionThunk(values));
    return null;
  };

  const renderSaveFormButton = () => {
    if (permissions.edit === true && !currentYearFrozen) {
      return <Button type={'submit'} text={vocabulary.buttons.save} />;
    }
  };

  const renderDeleteButton = (id: number | null) => {
    if (!id) {
      return null;
    }

    if (permissions.remove === true && !currentYearFrozen) {
      return (
        <Button
          className={'delete-button'}
          text={vocabulary.buttons.delete}
          onClick={() => showConfirmationModal(id)}
        />
      );
    }
  };

  const showConfirmationModal = (id: number) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmationModal onClose={onClose} onOk={() => handleDelete(id)} />
        );
      },
    });
  };

  const handleDelete = (id: number) => {
    if (id) {
      dispatch(removeDivisionThunk(id));
      dispatch(removeDivisionFromStore());
      dispatch(getDivisionsThunk());
      dispatch(getAgenciesWithDivisionsThunk());
      dispatch(closeModal({ name: MODAL_DIVISION }));
    }
  };

  const renderReportButton = (id: number | null, type: 'pdf' | 'docx') => {
    if (!id) {
      return null;
    }

    if (permissions.printReport === true) {
      return (
        <Button
          text={
            type === 'pdf'
              ? vocabulary.buttons.reports.downloadReportPdf
              : vocabulary.buttons.reports.downloadReportDocx
          }
          onClick={() => downloadDivisionReport(id, type)}
        />
      );
    }
  };

  const downloadDivisionReport = (divisionId: number, type: 'pdf' | 'docx') => {
    setLocked(true);

    reportsService.downloadDivisionReport(divisionId, type);
  };

  const renderAssignedAgency = (agencyId: number | null) => {
    return agencyId ? (
      <p className={'division-assigned__text'}>
        Zugewiesene Abteilung: <span>{getAgencyName(agencyId)}</span>
      </p>
    ) : (
      <p className={'division-assigned__text'}>Keiner Abteilung zugewiesen</p>
    );
  };

  const getAgencyName = (id: number) => {
    const agency = agencies.list.find((agency) => agency.id === id);
    return agency?.title ? agency.title : '';
  };

  const renderAssignedPerson = (peopleId: number | null) => {
    return peopleId ? (
      <p className={'division-assigned__text'}>
        Zugewiesene Person: <span>{getPeopleName(peopleId)}</span>
      </p>
    ) : (
      <p className={'division-assigned__text'}>Keiner Person zugewiesen </p>
    );
  };

  const getPeopleName = (id: number) => {
    return people.list.find((person) => person.id === id)?.name ?? '';
  };

  const handleAssignPeople = (e, values) => {
    e.preventDefault();
    dispatch(setCurrentDivision(values));
    dispatch(openModal({ name: ASSIGN_PEOPLE_MODAL, data: 'division' }));
  };

  useEffect(() => {
    dispatch(getPeopleThunk());
  }, [dispatch]);

  useEffect(() => {
    const unlock = () => setLocked(false);
    document.body.addEventListener(events.UNLOCK.name, unlock);

    return () => {
      document.body.removeEventListener(events.UNLOCK.name, unlock);
    };
  }, []);

  const missionsInformationEditor = useEditor({
    extensions: [StarterKit],
    content: divisions.current?.missions,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      dispatch(setDivisionMissions(html));
      setFieldValueRef.current?.('missions', html);
      setMissionsFocusPosition(editor.state.selection.from);
    },
  });

  const environmentsInformationEditor = useEditor({
    extensions: [StarterKit],
    content: divisions.current?.environments,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      dispatch(setDivisionEnvironments(html));
      setFieldValueRef.current?.('environments', html);
      setEnvironmentsFocusPosition(editor.state.selection.from);
    },
  });

  return (
    <Formik
      key={formKey}
      enableReinitialize={false}
      initialValues={initialValues}
      onSubmit={async (values: DivisionFormFormikState, helpers) => {
        const updated = await onSubmit(initialValues.id, values);

        if (!updated && divisions.isCreateUpdateDivisionThunkPending) {
          return;
        }

        helpers.resetForm({
          values: updated ? divisionToFormValues(updated, values) : values,
        });
      }}
    >
      {({ values, errors, touched, dirty, setFieldValue }) => {
        setFieldValueRef.current = setFieldValue;
        return (
          <Form>
            <CheckIfFormIsDirty dirty={dirty} />
            <FixedHeader>
              <FormHeader
                renderSave={renderSaveFormButton()}
                renderDelete={renderDeleteButton(initialValues.id)}
                renderReport={renderReportButton(initialValues.id, 'pdf')}
                renderDocxReport={renderReportButton(initialValues.id, 'docx')}
              />

              <Container>
                <div className="field-wrapper">
                  <InputField
                    name="title"
                    className="form__field_big bordered"
                    validate={validateField}
                    placeholder={
                      vocabulary.forms.division.placeholders.divisionTitle
                    }
                    forDivision
                    currentYearFrozen={currentYearFrozenLocal}
                  />
                  {errors.title && touched.title && (
                    <p className={'field-error'}>{errors.title}</p>
                  )}
                </div>
              </Container>
            </FixedHeader>

            <FormBody>
              <GrayPanel text={vocabulary.forms.division.description} first />
              <Container>
                <div className={'division-assigned'}>
                  {renderAssignedAgency(initialValues.AgencyId)}
                  {renderAssignedPerson(initialValues.PeopleId)}
                  {shouldChangeAssign && (
                    <CreateButton
                      text={
                        initialValues.PeopleId
                          ? vocabulary.buttons.reassignPerson
                          : vocabulary.buttons.assignPeople
                      }
                      onClick={(e) => handleAssignPeople(e, values)}
                    />
                  )}
                </div>
              </Container>
              <div className="division-wrapper">
                <div className={'form-panel-gray'}>
                  <Container>
                    <div className={'row'}>
                      <div className={'col col_half'}>
                        <p className={'form-panel-gray__text'}>
                          {vocabulary.labels.mission}
                        </p>
                      </div>
                      <div className={'col col_half'}>
                        <p className={'form-panel-gray__text'}>
                          {vocabulary.labels.environment}
                        </p>
                      </div>
                    </div>
                  </Container>
                </div>
                <Container>
                  <div className={'row'}>
                    <div className={'col col_half'}>
                      <TextEditor
                        position={missionsFocusPosition}
                        editor={missionsInformationEditor}
                        commentText={values.missions || ''}
                      />
                    </div>
                    <div className={'col col_half'}>
                      <TextEditor
                        position={environmentsFocusPosition}
                        editor={environmentsInformationEditor}
                        commentText={values.environments || ''}
                      />
                    </div>
                  </div>
                </Container>

                <DivisionFormActivities
                  activities={values.Activities}
                  values={values}
                  yearId={yearId}
                  yearName={yearName}
                  currentYearFrozen={currentYearFrozenLocal}
                />

                <div className={'form-panel-gray'}>
                  <Container>
                    <div className={'row'}>
                      <div className={'division-col division-col_title'}>
                        <p className={'form-panel-gray__text'}>
                          {vocabulary.labels.midTermGoal}
                        </p>
                      </div>
                      <div className={'division-col division-col_title'}>
                        <div className={'division-title-wrapper'}>
                          <p className={'form-panel-gray__text_50'}>
                            {vocabulary.labels.shortTermGoal}
                          </p>
                          <p className={'form-panel-gray__text_50'}>
                            {vocabulary.labels.outcome}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Container>
                </div>

                {initialValues.AgencyId &&
                agencies.current &&
                agencies.current.MidtermGoals &&
                agencies.current.MidtermGoals.length > 0 ? (
                  agencies.current.MidtermGoals.map(
                    (midTermGoal, midTermGoalIndex) => (
                      <div
                        className={
                          midTermGoalIndex > 0
                            ? 'container container_division'
                            : 'container container_division'
                        }
                        key={midTermGoalIndex}
                      >
                        <div className={'row'}>
                          <div className={'division-col division-col_row'}>
                            <div className={'division-mtg'}>
                              <p className={'division-mtg__text'}>
                                {midTermGoal.title}
                              </p>
                            </div>
                            <div className={'division-mtg'}>
                              <p className={'division-mtg__text'}>
                                {midTermGoal.description}
                              </p>
                            </div>
                          </div>
                          <div className={'division-col'}>
                            <DivisionFormStg
                              values={values}
                              midTermGoal={midTermGoal}
                              yearId={yearId}
                              currentYearFrozen={currentYearFrozenLocal}
                            />
                          </div>
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <Container>
                    {initialValues.AgencyId ? (
                      <NotFoundMessage
                        message={
                          'Nicht gefunden: Legislaturziele konnte nicht gefunden werden. '
                        }
                      />
                    ) : (
                      <NotFoundMessage
                        message={'Kein(e) agency zugewiesen. '}
                      />
                    )}
                  </Container>
                )}
                {divisions.isCreateUpdateDivisionThunkPending && (
                  <Spinner isFullSize />
                )}
              </div>
            </FormBody>
            {locked && (
              <Spinner
                isFullSize
                message={vocabulary.preloader.waitForReport}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
